
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PerformanceGoal } from "../services/performanceService";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

export const usePerformanceGoals = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['performance-goals', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from('performance_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', activeYear)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching performance goals:', error);
        throw error;
      }

      return (data || []) as PerformanceGoal[];
    },
  });
};

export const usePerformanceAppraisals = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['performance-appraisals', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from('performance_appraisals')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', activeYear)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching performance appraisals:', error);
        throw error;
      }

      return data || [];
    },
  });
};

export const useTeamMemberAppraisals = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['team-member-appraisals', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Fetch appraisals where current user is the supervisor
      const { data, error } = await supabase
        .from('performance_appraisals')
        .select('*')
        .eq('supervisor_id', user.id)
        .eq('year', activeYear)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching team member appraisals:', error);
        throw error;
      }

      return data || [];
    },
  });
};

export const usePerformanceBands = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['performance-bands', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from('performance_bands')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', activeYear)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching performance bands:', error);
        throw error;
      }

      return data || [];
    },
  });
};

export const useTeamMemberBands = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['team-member-bands', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Fetch bands where current user is the supervisor
      const { data, error } = await supabase
        .from('performance_bands')
        .select('*')
        .eq('supervisor_id', user.id)
        .eq('year', activeYear)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching team member bands:', error);
        throw error;
      }

      return data || [];
    },
  });
};

export const useBandDisagreements = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['band-disagreements', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Query will automatically filter based on RLS policies and year:
      // - Users can see their own disagreements
      // - Supervisors can see their team's disagreements
      const { data, error } = await supabase
        .from('band_disagreements')
        .select('*')
        .eq('year', activeYear)
        .order('submitted_date', { ascending: false });

      if (error) {
        console.error('Error fetching band disagreements:', error);
        throw error;
      }

      return data || [];
    },
  });
};

export const useBandingSimulation = () => {
  const { activeYear } = usePerformanceYear();
  
  return useQuery({
    queryKey: ['banding-simulation', activeYear],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from('banding_simulation')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', activeYear)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching banding simulation:', error);
        throw error;
      }

      return data;
    },
  });
};

export const useTeamMembers = () => {
  return useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('supervisor_id', user.id)
        .order('full_name', { ascending: true });

      if (error) {
        console.error('Error fetching team members:', error);
        throw error;
      }

      return data || [];
    },
  });
};
